/**
 * <hy-cart-page>: AJAX-Erweiterung für die bestehende Warenkorbseite
 * (sections/hy-main-cart.liquid), ohne deren Markup zu verändern (AP-66).
 * Fängt Änderungen am bestehenden Mengenfeld (name="updates[…]") und Klicks
 * auf den bestehenden Entfernen-Link (item.url_to_remove) ab — beide bleiben
 * ohne dieses Modul exakt das, was sie schon immer waren: ein klassisches
 * Formularfeld und ein echter Link. Netzwerkfehler fallen jeweils auf genau
 * den Pfad zurück, der ohne JavaScript ohnehin gelaufen wäre (Formular-
 * Submit bzw. echte Navigation zum Entfernen-Link).
 *
 * Dieselbe Mutationslogik wie assets/hy-cart-drawer.js (line-Adressierung,
 * bundled section rendering, aria-busy statt disabled) — bewusst dupliziert
 * statt geteilt: tools/check-budgets.mjs folgt keinen ES-imports, ein
 * gemeinsames Modul wäre für das Budget-Werkzeug unsichtbar.
 */
/**
 * Maximum quantity per order (AP-295). snippets/hy-quantity-input.liquid puts
 * the limit and the room left for this line (limit minus every other line of
 * the same product) on the field; snippets/hy-quantity-limit-note.liquid
 * renders the hidden note in the row. Same helpers as assets/hy-cart-drawer.js
 * — duplicated on purpose, see the module comment.
 */
function limitRoom(input) {
  if (!input || input.dataset.hyLimit === undefined) return Infinity;
  return Number(input.dataset.hyLimitRoom);
}

function revealLimitNote(row) {
  row?.querySelector('[data-hy-quantity-limit-note] > [hidden]')?.removeAttribute('hidden');
}

class HyCartPage extends HTMLElement {
  #controller = null;
  #pending = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('change', this.#handleQuantityChange, { signal });
    this.addEventListener('click', this.#handleRemoveClick, { signal });
    this.addEventListener('click', this.#handleStepper, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #handleQuantityChange = (event) => {
    const input = event.target.closest('input[type="number"]');
    if (!input || !this.contains(input)) return;

    const row = input.closest('.hy-main-cart__row');
    const line = Number(row?.dataset.hyLine);
    if (!line) return;

    if (this.#pending) return;
    let quantity = Math.max(0, Number(input.value) || 0);
    // Above the maximum quantity: save the highest allowed quantity instead
    // and show the note (AP-295). Nothing to save when that is the quantity
    // the line already has.
    const room = limitRoom(input);
    const limitReached = quantity > room;
    if (limitReached) {
      quantity = Math.max(room, Number(input.min) || 1);
      input.value = String(quantity);
      if (quantity === Number(input.defaultValue)) {
        revealLimitNote(row);
        return;
      }
    }
    const title = row.querySelector('.hy-main-cart__item-link span')?.textContent || '';
    this.#mutate(line, quantity, input, {
      removed: quantity === 0,
      title,
      limitReached,
      fallback: () => this.querySelector('#hy-cart-form')?.submit(),
    });
  };

  /**
   * Minus/plus of the quantity pill (AP-258). Same approach as
   * assets/hy-product-form.js: stepUp/stepDown respect min/max/step from the
   * markup, then a 'change' event hands over to #handleQuantityChange.
   * Delegated on the element, so steppers inside freshly swapped content work
   * without re-arming. Ignored while a mutation is pending, otherwise the
   * field would show a value that was never saved.
   */
  #handleStepper = (event) => {
    const button = event.target.closest('[data-hy-stepper-step]');
    if (!button || !this.contains(button) || this.#pending) return;

    const input = button.parentElement?.querySelector('input[type="number"]');
    if (!input) return;

    const before = input.value;
    const direction = Number(button.dataset.hyStepperStep);
    // At the maximum quantity per order plus shows the note instead (AP-295).
    if (direction > 0 && (Number(input.value) || 0) + (Number(input.step) || 1) > limitRoom(input)) {
      revealLimitNote(button.closest('.hy-main-cart__row'));
      return;
    }
    try {
      if (direction > 0) input.stepUp();
      else input.stepDown();
    } catch {
      const step = Number(input.step) || 1;
      const min = Number(input.min) || 0;
      const max = input.max === '' ? Infinity : Number(input.max);
      const current = Number(input.value) || min;
      input.value = String(Math.min(max, Math.max(min, current + direction * step)));
    }
    // At min/max nothing changed: no request, no "quantity updated" message.
    if (input.value === before) return;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #handleRemoveClick = (event) => {
    const removeLink = event.target.closest('.hy-main-cart__remove');
    if (!removeLink || !this.contains(removeLink)) return;

    event.preventDefault();

    const row = removeLink.closest('.hy-main-cart__row');
    const line = Number(row?.dataset.hyLine);
    if (!line) return;

    const title = row.querySelector('.hy-main-cart__item-link span')?.textContent || '';
    const href = removeLink.href;

    // GA4 remove_from_cart (M6 AP-95): Item der Zeile lesen, bevor sie durch
    // den /cart/change.js-Austausch verschwindet.
    const dl = row.querySelector('[data-hy-dl-line]');
    if (dl) {
      try {
        document.dispatchEvent(
          new CustomEvent('hy:cart:removed', { detail: { item: JSON.parse(dl.textContent) } }),
        );
      } catch (error) {
        /* defekte Insel: kein Event, Entfernen läuft normal weiter */
      }
    }

    this.#mutate(line, 0, removeLink, {
      removed: true,
      title,
      fallback: () => window.location.assign(href),
    });
  };

  #mutate(line, quantity, control, { removed, title, limitReached, fallback } = {}) {
    if (this.#pending) return;
    this.#pending = true;

    control?.setAttribute('aria-busy', 'true');

    const body = new URLSearchParams();
    body.append('line', String(line));
    body.append('quantity', String(quantity));
    body.append('sections', this.#sectionIds().join(','));
    body.append('sections_url', window.location.pathname + window.location.search);

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
      body: body.toString(),
    })
      .then(async (response) => {
        const responseJson = await response.json();

        if (!response.ok) {
          this.#showStatus(responseJson.description || responseJson.message);
          return;
        }

        this.#applySections(responseJson.sections);
        this.#showStatus(
          removed
            ? this.dataset.removedPrefix + title + this.dataset.removedSuffix
            : this.dataset.quantityUpdated,
        );
        this.#restoreFocus(line, removed);

        document.dispatchEvent(
          new CustomEvent('hy:cart:updated', {
            detail: { cart: responseJson, sections: responseJson.sections },
          }),
        );
        // After the event: its listeners swap the cart markup once more
        // (the drawer updates the cart page and itself), which would hide the
        // note again (AP-295).
        if (limitReached) {
          revealLimitNote(this.querySelector(`.hy-main-cart__row[data-hy-line="${line}"]`));
        }
      })
      .catch(() => {
        fallback?.();
      })
      .finally(() => {
        control?.removeAttribute('aria-busy');
        this.#pending = false;
      });
  }

  #sectionIds() {
    const ids = [];
    const headerId = document.querySelector('.hy-header')?.id.replace('shopify-section-', '');
    if (headerId) ids.push(headerId);
    const drawerId = document.querySelector('hy-cart-drawer')?.dataset.sectionId;
    if (drawerId) ids.push(drawerId);
    if (this.dataset.sectionId) ids.push(this.dataset.sectionId);
    return ids;
  }

  #applySections(sections) {
    if (!sections) return;

    const headerId = document.querySelector('.hy-header')?.id.replace('shopify-section-', '');
    if (headerId && sections[headerId]) {
      const headerDoc = new DOMParser().parseFromString(sections[headerId], 'text/html');
      const newCartLink = headerDoc.querySelector('.hy-header__cart-link');
      const currentCartLink = document.querySelector('.hy-header__cart-link');
      if (newCartLink && currentCartLink) currentCartLink.replaceWith(newCartLink);
    }

    const ownHtml = sections[this.dataset.sectionId];
    if (ownHtml) {
      const ownDoc = new DOMParser().parseFromString(ownHtml, 'text/html');
      const newContent = ownDoc.querySelector('.hy-main-cart__content');
      const oldContent = this.querySelector('.hy-main-cart__content');
      if (newContent && oldContent) oldContent.replaceWith(newContent);
    }

    // Steht der Drawer trotz /cart im DOM (er bleibt technisch erreichbar),
    // seinen Inhalt ebenfalls aktualisieren — assets/hy-cart-drawer.js macht
    // dasselbe für den umgekehrten Fall.
    const drawer = document.querySelector('hy-cart-drawer');
    const drawerHtml = drawer && sections[drawer.dataset.sectionId];
    if (drawerHtml) {
      const drawerDoc = new DOMParser().parseFromString(drawerHtml, 'text/html');

      const newItems = drawerDoc.querySelector('.hy-cart-drawer__items');
      const oldItems = drawer.querySelector('.hy-cart-drawer__items');
      if (newItems && oldItems) oldItems.replaceWith(newItems);

      const newSummary = drawerDoc.querySelector('.hy-cart-drawer__summary');
      const oldSummary = drawer.querySelector('.hy-cart-drawer__summary');
      // Same three cases as assets/hy-cart-drawer.js (AP-257): an empty
      // cart renders no __summary, so it may need inserting.
      const currentItems = drawer.querySelector('.hy-cart-drawer__items');
      if (newSummary && oldSummary) oldSummary.replaceWith(newSummary);
      else if (newSummary && currentItems) currentItems.after(newSummary);
      else if (!newSummary && oldSummary) oldSummary.remove();
    }
  }

  #restoreFocus(line, wasRemoval) {
    // Ein Frame verzögert — dieselbe, live in AP-65 gefundene Notwendigkeit:
    // ein Element innerhalb des per replaceWith() frisch eingefügten
    // Teilbaums ist erst nach einem Layout-Durchlauf zuverlässig
    // fokussierbar.
    requestAnimationFrame(() => {
      const rows = Array.from(this.querySelectorAll('.hy-main-cart__row'));

      if (rows.length === 0) {
        this.querySelector('.hy-main-cart__empty')
          ?.closest('.hy-main-cart__content')
          ?.querySelector('a')
          ?.focus();
        return;
      }

      const target = rows.find((el) => Number(el.dataset.hyLine) === line) || rows[rows.length - 1];
      const control = wasRemoval
        ? target.querySelector('.hy-main-cart__remove')
        : target.querySelector('input[type="number"]');
      control?.focus();
    });
  }

  #showStatus(message) {
    const statusEl = this.querySelector('.hy-main-cart__status');
    if (statusEl) statusEl.textContent = message || '';
  }
}

customElements.define('hy-cart-page', HyCartPage);
